import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import PhotoActionModal from '../PhotoActionModal';
import PhotoCaptureModalEnhanced, { PhotoCaptureResult } from '../PhotoCaptureModalEnhanced';
import PhotoIPCameraModal, { PhotoResult as IPCameraResult } from '../PhotoIPCameraModal';
import { api } from '../../services/api';
import { appendIdentityPhoto } from '../../services/identityPhotoUpload';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import { photoCaptureSettings } from './photoCaptureSettings';

/** What is being photographed, and what it replaces. */
export interface PhotoCaptureRequest {
  /**
   * The `identity_photos` row this capture replaces, when there is one.
   *
   * Absent means add: a slot that is empty, or a photo that only exists in a legacy
   * `people.document_*_url` column and so has no row to delete.
   */
  photoId?: string;
  /** The photo already in place, preloaded so the operator can see what they are replacing. */
  url?: string;
  /** `photo_type` for the upload. Absent means the identity photo. */
  photoType?: string;
  /**
   * The vehicle this photo belongs to, when it is a vehicle photo.
   *
   * A vehicle photo lives on the VEHICLE record. Posting it to /people/:id/photos writes an
   * identity_photos row of type "vehicle" that neither the gallery nor the sidebar ever shows
   * — both read the vehicle first — so the update would report success and change nothing.
   */
}

export interface PhotoCaptureFlowHandle {
  /** Re-capture over the photo in place — straight into the editor. */
  update(request: PhotoCaptureRequest): void;
  /** Capture a photo from scratch — choose a method first. */
  create(request: PhotoCaptureRequest): void;
}

interface PhotoCaptureFlowProps {
  personId: string;
  customerId?: string;
  /** Called after a successful upload, to refetch the person. */
  onSaved: () => void;
  darkMode?: boolean;
}

type Stage = 'picker' | 'editor' | 'ipcamera';

/**
 * Every route to putting a photo on a person: pick a method, crop it or pull a frame off an IP
 * camera, upload it, replace what it supersedes.
 *
 * Mounted once per screen and driven through its ref, so the lightbox, the Documentos tab and
 * the profile page all run the SAME flow. They did not: each had its own, and a document
 * photographed from the tab got the identity photo's square crop, no preview of what it was
 * replacing, and face detection hunting for a face in a driving licence.
 *
 * What differs per photo kind — title, crop, which cameras, whether to look for a face — is
 * one table in photoCaptureSettings.ts rather than a condition at each call site.
 */
export const PhotoCaptureFlow = forwardRef<PhotoCaptureFlowHandle, PhotoCaptureFlowProps>(
  ({ personId, customerId, onSaved, darkMode = false }, ref) => {
    const [request, setRequest] = useState<PhotoCaptureRequest | null>(null);
    const [stage, setStage] = useState<Stage | null>(null);
    const [editorTab, setEditorTab] = useState<0 | 1>(0);

    useImperativeHandle(ref, () => ({
      update: (next) => {
        setRequest(next);
        setEditorTab(0);
        setStage('editor');
      },
      create: (next) => {
        setRequest(next);
        setStage('picker');
      },
    }));

    /// Set by the picker's own options, read by its `onClose`. See `dismissPicker`.
    const advancing = useRef(false);

    const close = () => {
      setStage(null);
      setRequest(null);
    };

    const advanceTo = (next: Stage, tab?: 0 | 1) => {
      advancing.current = true;
      if (tab !== undefined) setEditorTab(tab);
      setStage(next);
    };

    /**
     * PhotoActionModal reports "chose an option" and "cancelled" the same way: it calls the
     * option handler and then `onClose()`, synchronously, in one React event.
     *
     * Every other caller survives that because its `onClose` only hides the picker — a no-op on
     * something already hidden. This flow's `close()` also clears `request`, and `if (!request)
     * return null` unmounts everything: `setStage('editor')` then `setRequest(null)` batch
     * together, the later write wins, and the Documentos tab's "adicionar foto da frente" opened
     * a dialog that vanished on the first click with nothing behind it.
     *
     * So the flag, rather than changing PhotoActionModal: eleven call sites depend on that
     * `onClose()` to dismiss their picker — VisitorRegistrationModal's three upload areas do
     * nothing else — and taking it away to fix one caller breaks the other ten.
     */
    const dismissPicker = () => {
      if (advancing.current) {
        advancing.current = false;
        return;
      }
      close();
    };

    const handleConfirm = async (result: PhotoCaptureResult | IPCameraResult) => {
      if (!request) return;
      try {
        // Only the identity photo keeps its untouched original beside the crop (migration 230);
        // a document or vehicle crop is not worth a second upload. An absent photo_type is what
        // the backend reads as the identity photo.
        const isIdentityPhoto = !request.photoType || request.photoType === 'facial_recognition';

        const formData = new FormData();
        appendIdentityPhoto(formData, isIdentityPhoto ? result : { blob: result.blob });
        if (customerId) formData.append('customer_id', customerId);
        if (request.photoType) formData.append('photo_type', request.photoType);

        // Delete first, or the person keeps both and which one shows comes down to the order
        // the API happens to return them in.
        if (request.photoId) {
          await api.delete(`/people/${personId}/photos/${request.photoId}`);
        }
        await api.post(`/people/${personId}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        toast.success('Foto atualizada com sucesso!');
        close();
        onSaved();
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Erro ao atualizar foto');
      }
    };

    if (!request) return null;

    const settings = photoCaptureSettings(request.photoType);

    return (
      <>
        <PhotoActionModal
          open={stage === 'picker'}
          onClose={dismissPicker}
          onUpload={() => advanceTo('editor', 0)}
          onWebcam={() => advanceTo('editor', 1)}
          onIPCamera={() => advanceTo('ipcamera')}
          title={settings.title}
          darkMode={darkMode}
        />

        <PhotoCaptureModalEnhanced
          open={stage === 'editor'}
          onClose={close}
          onConfirm={handleConfirm}
          title={settings.title}
          cropShape="rect"
          aspectRatio={settings.aspectRatio}
          customerId={customerId}
          existingPhoto={getAbsoluteImageUrl(request.url)}
          initialTab={editorTab}
          disableFaceDetection={settings.disableFaceDetection}
          hideIPCamera
        />

        <PhotoIPCameraModal
          open={stage === 'ipcamera'}
          onClose={close}
          onConfirm={handleConfirm}
          title={settings.title}
          customerId={customerId}
          filterCategory={settings.cameraCategory}
          cropShape="rect"
          aspectRatio={settings.aspectRatio}
          disableFaceDetection={settings.disableFaceDetection}
          darkMode={darkMode}
        />
      </>
    );
  },
);

PhotoCaptureFlow.displayName = 'PhotoCaptureFlow';
